import React, { useCallback } from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
  TransactionPage,
  FindTransactionsByUserIdAdminQueryVariables
} from "../../../../../schemaTypes";
import { newTransactionSubscription } from "../../../Transaction/FindTransactions";
import { WithFindTransactionsByUserId } from "../../../Transaction/FindTransactionsByUserId";

export const findTransactionsByUserIdAdminQuery = gql`
  query FindTransactionsByUserIdAdmin(
    $userId: String!
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findTransactionsByUserIdAdmin(
      userId: $userId
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        wallet {
          id
          balance
          owner {
            id
            admin{
              id
              merchantProfile{
                id
                companyName
                businessName
              }
            }
            customer {
              id
              firstname
              lastname
            }
          }
          createdAt
          updatedAt
        }
        recipientWallet {
          id
          balance
          currency
          owner {
            id
            admin {
              id
              merchantProfile {
                id
                companyName
                businessName
              }
            }
            customer {
              id
              firstname
              lastname
            }
          }
          createdAt
          updatedAt
        }
        bankPreference {
          id
        }
        offer {
          id
          title
          description
          category{
            name
          }
        }
        njoyCode
        partnerVoucher
        user {
          id
          # createdAt
          # updatedAt
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

interface Props {
  variables: FindTransactionsByUserIdAdminQueryVariables;
  children: (data: WithFindTransactionsByUserId) => React.ReactElement | null;
}

export const FindTransactionsByUserIdAdmin: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    { findTransactionsByUserIdAdmin: TransactionPage },
    FindTransactionsByUserIdAdminQueryVariables
  >
  (findTransactionsByUserIdAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  useSubscription(newTransactionSubscription);

  const transactions = data?.findTransactionsByUserIdAdmin?.data || [];
  const page = data?.findTransactionsByUserIdAdmin?.page || variables.page;
  const pageSize = data?.findTransactionsByUserIdAdmin?.pageSize || variables.pageSize;
  const total = data?.findTransactionsByUserIdAdmin?.total || 0;
  const totalPages = data?.findTransactionsByUserIdAdmin?.totalPages || 0;
  const hasMoreTransactions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findTransactionsByUserIdAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findTransactionsByUserIdAdmin: {
            ...fetchMoreResult?.findTransactionsByUserIdAdmin,
            data: [
              ...(prev?.findTransactionsByUserIdAdmin?.data || []),
              ...(fetchMoreResult?.findTransactionsByUserIdAdmin?.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any[],
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
  });
};